import {
  call, select, put,
} from 'redux-saga/effects';
import { CQ_ACTION_CODES, CQ_POTS } from '../catalogConstants';
import { WEBSERVICE_ERRORS } from '../errorManagement/errorCatalog';
import { actions as uiNeedsActions } from '../reducers/uiNeeds';
import { actions as obdActions } from '../reducers/obd';
import { commonActions } from '../reducers/common';
import { actions as apisNeedsActions } from '../reducers/apisNeeds';
import { initCQ, selectElements, initCQAfterSale, initCQTV } from './ascertain';
import { updateMyContacts, getClientData } from './client';
import { initCardPayment, initFundedPayment } from './payment';
import { MOBILE_INSURANCE } from '../bitConstants/constants';
import { sendMobileInsuranceToSE } from './se';
import { confirmCQ, getProvisionalAgreement } from './cq';
import { decreaseStock } from './catalog';
import { getSignatureOTP } from './r2r';
import { PRODUCT_TYPES } from '../utils/catalog';
import { megaMinderStatus } from '../minder/MegaMinder/megaMinderStatus';
import { INIT_TYPES } from '../flowDefinition/flowTypes';
import { getEveryAfterSaleParent, getMyProducts } from './products';

const apisNeedsStore = state => state.apiNeeds;
const offerStore = state => state.offer;
const contextStore = state => state.context.data;
const flowStore = state => state.flow;
const manualStore = state => state.manual;

/**
 * Función generadora que se encarga de realizar la funcionalidad del
 * boton continuar del paso de datos personales:
 * - llamar al API * actualizar/contacto (updateMyContacts).
 * - capturar y tratar posible error de email en lista negra
 * - llamar al api de initCQ si procede
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* continuePersonalDataStep(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const { originalPermanentResidenceInfo, obd, personalContactInfo, myProductsInfo } = apiNeeds;

  const { showError, normalizeHttpErrors } = params;
  const updateMyContactsParams = {
    showError,
    normalizeHttpErrors,
  };
  const {
    contactPhone,
    email,
  } = personalContactInfo;

  yield put(uiNeedsActions.setPersonalContact({
    contactPhone,
    email,
  }));
  const normalizedResponse = yield call(
    updateMyContacts,
    updateMyContactsParams,
  );

  if (
    normalizedResponse
    && normalizedResponse.response
    && normalizedResponse.response.status === 'KO'
    && normalizedResponse.response.error
    && normalizedResponse.response.error.codigoErrorNegocio
    && (normalizedResponse.response.error.codigoErrorNegocio
      === WEBSERVICE_ERRORS.INVALID_CONTACTS
      || normalizedResponse.response.error.codigoErrorNegocio === WEBSERVICE_ERRORS.INVALID_EMAIL)
  ) {
    yield put(uiNeedsActions.setBlockedUIStep(true));
    yield put(uiNeedsActions.setInvalidEmail());
  } else if (originalPermanentResidenceInfo && originalPermanentResidenceInfo.id && !normalizedResponse.blockedByOBD) {
    const { flowJson } = yield select(flowStore);

    const params = {
      showError,
      normalizeHttpErrors,
    };
    yield put(uiNeedsActions.setBlockedUIStep(false));

    if (!myProductsInfo) {
      yield call(getMyProducts, params);
    }

    if (flowJson.initType === INIT_TYPES.AFTERSALE) {
      yield call(getEveryAfterSaleParent, params);
    } else if (flowJson.initType === INIT_TYPES.TV) {
      yield call(initCQTV, params);
    } else if (flowJson.initType === INIT_TYPES.NETFLIX) {
      yield call(startChangeOTTModalityAscertain, params);
    } else {
      yield call(initCQ, params);
    }
  } else if (normalizedResponse.blockedByOBD) {
    yield put(uiNeedsActions.resetLastConfirmedStep());
    yield put(uiNeedsActions.setBlockedUIStep(true));
    const { validLines } = obd;
    if (validLines && validLines.length === 1) {
      yield put(obdActions.generatePin(validLines[0]));
    }
  } else {
    yield put(uiNeedsActions.setBlockedUIStep(false));
  }
}

/**
 * Función generadora que se encarga de realizar la funcionalidad del
 * boton continuar del paso de producto a contratar cambiado
 */
export function* continueProductOfferingToBuyChangedStep() {  
  yield put(uiNeedsActions.setBlockedUIStep(false));
}

export function* continueDeviceSummaryStep(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const { phoneNumberOTP } = apiNeeds;
  if (phoneNumberOTP) {
    yield call(getSignatureOTP, params);
  }
}

/**
 * Función generadora que se encarga de realizar la funcionalidad del
 * boton continuar del paso de datos personales:
 * - llamar al API * actualizar/contacto (updateMyContacts).
 * - capturar y tratar posible error de email en lista negra
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* continuePersonalDataStepWithoutInitCQ(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const { originalPermanentResidenceInfo, obd } = apiNeeds;

  const { showError, normalizeHttpErrors } = params;
  const updateMyContactsParams = {
    showError,
    normalizeHttpErrors,
  };
  const normalizedResponse = yield call(
    updateMyContacts,
    updateMyContactsParams,
  );

  if (
    normalizedResponse
    && normalizedResponse.response
    && normalizedResponse.response.status === 'KO'
    && normalizedResponse.response.error
    && normalizedResponse.response.error.codigoErrorNegocio
    && (normalizedResponse.response.error.codigoErrorNegocio
      === WEBSERVICE_ERRORS.INVALID_CONTACTS
      || normalizedResponse.response.error.codigoErrorNegocio === WEBSERVICE_ERRORS.INVALID_EMAIL)
  ) {
    yield put(uiNeedsActions.setBlockedUIStep(true));
    yield put(uiNeedsActions.setInvalidEmail());
  } else if (originalPermanentResidenceInfo && originalPermanentResidenceInfo.id && !normalizedResponse.blockedByOBD) {
    yield put(uiNeedsActions.setBlockedUIStep(false));
    yield call(getClientData, params);
  } else if (normalizedResponse.blockedByOBD) {
    yield put(uiNeedsActions.resetLastConfirmedStep());
    yield put(uiNeedsActions.setBlockedUIStep(true));
    const { validLines } = obd;
    if (validLines && validLines.length === 1) {
      yield put(obdActions.generatePin(validLines[0]));
    }
  } else {
    yield put(uiNeedsActions.setBlockedUIStep(false));
    yield call(getClientData, params);
  }
}

export function* continueVTLFlow(params) {
  const offer = yield select(offerStore);
  const apisNeeds = yield select(apisNeedsStore);
  const { deviceInfo, poptSelected } = apisNeeds;
  const { mobileInsurance } = deviceInfo;
  const { name, modality } = { ...mobileInsurance };


  const { name: productOfferingName, color, storage, tradeIn } = deviceInfo;

  const { storageId, colorId, productOffering } = offer;

  const terminalData = {
    productOfferingName,
    productOffering,
    colorId,
    storageId,
    selectedMobileInsuranceValue: modality,
    selectedMobileInsuranceLabel: name,
    color,
    storage,
    poptSelected,
    tradeIn,
  };


  if (poptSelected === CQ_POTS.PAYMENT_POT.CARD) {
    // Guardar información en sesión para el envio a SE
    sessionStorage.setItem('terminalDataInfo', JSON.stringify(terminalData));

    yield call(
      initCardPayment,
      params,
    );
  } else {
    const urlSupplier = yield call(
      initFundedPayment,
      params,
    );

    if (urlSupplier) {
      if (terminalData && terminalData.selectedMobileInsuranceValue && terminalData.selectedMobileInsuranceValue !== MOBILE_INSURANCE.NS_OPTION) {
        const sendMobileInsuranceParams = {
          ...params,
          terminalData,
        };
        yield call(
          sendMobileInsuranceToSE,
          sendMobileInsuranceParams,
        );
      }

      window.location.href = urlSupplier;
      
      return megaMinderStatus.HALT;
    }
  }
}
// TODO Si hay error JS no se pone tapon
export function* continueToConfirmation(params) {
  const context = yield select(contextStore);
  const apisNeeds = yield select(apisNeedsStore);
  const { vtlDataForConfirmation } = apisNeeds;
  const { terminalData } = vtlDataForConfirmation;

  if (terminalData) {
    const {
      selectedMobileInsuranceValue,
      productOffering,
      colorId,
      storageId,
    } = terminalData;

    const sendMobileInsuranceParams = {
      params,
      terminalData,
    };
    const decreaseStockParams = {
      productOffering,
      colorId,
      storageId,
    };

    decreaseStock(decreaseStockParams, context);

    if (selectedMobileInsuranceValue && selectedMobileInsuranceValue !== MOBILE_INSURANCE.NS_OPTION) {
      yield call(
        sendMobileInsuranceToSE,
        sendMobileInsuranceParams,
      );
    }
  } else {
    // yield put(showError(handledErrors.BUSINESS_ERRORS.INCORRECT_PARAMS));
  }
}

export function* continueToR2RNPackConfirmation(params) {
  const context = yield select(contextStore);
  const offer = yield select(offerStore);
  const apisNeeds = yield select(apisNeedsStore);
  const { deviceInfo } = apisNeeds;
  const { mobileInsurance } = deviceInfo;
  const { modality } = { ...mobileInsurance };
  const { name: productOfferingName, color, storage } = deviceInfo;

  const { storageId, colorId, productOffering, poR, r2rInfo } = offer;

  let deviceProductOffering = productOffering;
  if (poR) {
    deviceProductOffering = poR;
  }

  if (r2rInfo?.length > 1) {
    r2rInfo.forEach(r2r => {
      const { poId, colorId, storageId } = r2r;

      const decreaseStockParams = {
        productOffering: poId,
        colorId,
        storageId,
      };

      decreaseStock(decreaseStockParams, context);
    })
    /** yield all(r2rInfo.map(idPO => call(getProductOfferingData, {
            normalizeHttpErrors, reduxUpdate, idPO,
          })));**/
  } else {
    const terminalData = {
      productOfferingName,
      productOffering: deviceProductOffering,
      colorId,
      storageId,
      selectedMobileInsuranceValue: modality,
      color,
      storage,
      // selectedPaymentMethod,
    };
  
    const decreaseStockParams = {
      productOffering: deviceProductOffering,
      colorId,
      storageId,
    };
    
    decreaseStock(decreaseStockParams, context);
    
    const sendMobileInsuranceParams = {
      params,
      terminalData,
    };
    if (modality && modality !== MOBILE_INSURANCE.NS_OPTION) {
      yield call(
        sendMobileInsuranceToSE,
        sendMobileInsuranceParams,
      );
    }
  }

  yield call(confirmCQ, params);
}

export function* continueToProvisionalAgreement(params) { // TODO REVISAR
  const apiNeeds = yield select(apisNeedsStore);
  const { poshtSelected, addressInfo, poitSelected } = apiNeeds;

  if (poshtSelected && poshtSelected === CQ_POTS.SHIPPING_POT.HOME_SHIPPING_ID) {
    if (addressInfo) {
      yield call(getProvisionalAgreement, params);
    }
    const flowInfo = yield select(flowStore)
    if (poitSelected === CQ_POTS.INSTALLATION_POT.INSTALLATION_ID && flowInfo.flowJson.sendInstallationAddressIfInstallerSelected) {
      yield call(getProvisionalAgreement, params);
    }
  } else {
    yield call(getProvisionalAgreement, params);
  }
}

export function* continuePackSummaryStep(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const { poR } = yield select(offerStore);

  if (poR) {
    const { phoneNumberOTP, phoneNumbersOTPToSelect } = apiNeeds;
    if (phoneNumberOTP) {
      yield call(getSignatureOTP, params);
    } else if (!phoneNumbersOTPToSelect || phoneNumbersOTPToSelect.length === 0) {
      yield call(continueToR2RNPackConfirmation, params);
    }
  } else {
    yield call(confirmCQ, params);
  }
}

export function* continueToSelectElements(params) {
  const apisNeeds = yield select(apisNeedsStore);
  const { selectElementsSecondaryOptions, selectElementsNeeds } = apisNeeds;
  const { secondarySelection } = selectElementsNeeds;
  const PRODUCT_TYPES_TO_TEST = [PRODUCT_TYPES.LAND_LINE, PRODUCT_TYPES.MOBILE];

  let canContinue = true;

  PRODUCT_TYPES_TO_TEST.forEach((productTypeToTest) => {
    if (selectElementsSecondaryOptions[productTypeToTest] && selectElementsSecondaryOptions[productTypeToTest].choiceGroups) {
      const thereIsSomethingToSelect = selectElementsSecondaryOptions[productTypeToTest].choiceGroups.find(choiceGroup => (
        choiceGroup.customerQuoteItems.length > 1
        && choiceGroup.canBeUnselectable === false
      ));

      if (thereIsSomethingToSelect) {
        const secondarySelected = secondarySelection.find(secondarySelect => secondarySelect.productType === productTypeToTest);
        if (!secondarySelected) {
          canContinue = false;
        }
      }
    }
  });

  if (canContinue) {
    yield call(selectElements, params);
  }
}

export function* continueAfterSaleParentProductSelector(params) {
  const apisNeeds = yield select(apisNeedsStore);
  const { productOffering, cisi} = yield select(offerStore);
  
  if (cisi) {
    yield call(initCQAfterSale, params);

    return megaMinderStatus.OK;
  } else if (productOffering) {
    const { afterSaleParentProduct } = apisNeeds;
    if (afterSaleParentProduct) {
      yield put(commonActions.setParentProductSelected(afterSaleParentProduct))
      yield call(initCQ, params);
    }
    return megaMinderStatus.OK;
  }

  const statusKOToReturn = megaMinderStatus.KO;
  statusKOToReturn.errorInfo = 'Falta el cisi y parentProductType o el productOffering';
  return megaMinderStatus.KO;
}

export function* startChangeOTTModalityAscertain(params) {
  const apisNeeds = yield select(apisNeedsStore);
  const { cisi, netflixObj, ottProductsArray } = yield select(offerStore);
  const { tvProducts } = apisNeeds.myProductsInfo;

  if (cisi && netflixObj?.mode) {
    const { netflixPoId } = apisNeeds.myProductsInfo;

    if (netflixPoId) {
      yield put(commonActions.setAfterSaleParentProduct({
        id: netflixPoId,
      }));
    }

    yield call(initCQAfterSale, {...params, productOfferingId: netflixPoId});

    return megaMinderStatus.OK;
  } else if(cisi && Array.isArray(ottProductsArray) && ottProductsArray.length > 0 && tvProducts[ottProductsArray[0].productOffering]){

    yield put(commonActions.setAfterSaleParentProduct({
      id: tvProducts[ottProductsArray[0].productOffering],
    }));

    yield call(initCQAfterSale, {...params, productOfferingId: ottProductsArray[0].productOffering});

    return megaMinderStatus.OK;
  }

  const statusKOToReturn = megaMinderStatus.KO;
  statusKOToReturn.errorInfo = 'Falta el cisi y parentProductType o el productOffering';
  return megaMinderStatus.KO;
}

export function* continueMultiSimFlow(params) {
  const { selectElementsNeeds, selectElementsSecondaryOptions } = yield select(apisNeedsStore);
  const { multiSim } = yield select(manualStore);

  const { numberOfSims, typeOfSims } = multiSim;

  if (numberOfSims > 0 && selectElementsSecondaryOptions?.[PRODUCT_TYPES.MULTI_SIM]?.choiceGroups) {
    const newSelectElementsNeeds = JSON.parse(JSON.stringify(selectElementsNeeds));

    const optionGroups = selectElementsSecondaryOptions[PRODUCT_TYPES.MULTI_SIM].choiceGroups;

    for (let i = 0; i < numberOfSims; i++) {
      const { customerQuoteItems, id } = optionGroups[i];

      if (!newSelectElementsNeeds?.secondarySelection) {
        newSelectElementsNeeds.secondarySelection = [];
      }

      newSelectElementsNeeds.secondarySelection.push({
        customerQuoteItems,
        id,
        groupIndex: 0,
        productType: PRODUCT_TYPES.MULTI_SIM,
        actionId: CQ_ACTION_CODES.REGISTER,
        selectedByClient: true,
      });
    }

    yield put(apisNeedsActions.saveActionInfoForApi(newSelectElementsNeeds));

    yield call(selectElements, params);
  }
}
